/**
 * Art helpers for the client.
 *
 * SVG is generated at runtime and inlined as a data URI rather than fetched from
 * /art/generated. Two reasons: no network round trip for an asset we can compute,
 * and the generated directory is gitignored, so a fresh clone would otherwise
 * render nothing until someone remembered to run `pnpm gen:art`.
 */

import {
  renderDie,
  renderFx,
  renderItemIcon,
  renderPortrait,
  renderCard,
  renderRoomTile,
  type CardOverlay,
  type CardType,
  type DiceFace,
  type DiceSkin,
  type FxKind,
  type ItemSlot,
  type Rarity,
  type RoomKind,
} from '@lastroll/art';

const cache = new Map<string, string>();

function toDataUri(svg: string): string {
  // encodeURIComponent keeps this correct for the Korean text inside portraits,
  // which btoa would throw on.
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function memo(key: string, make: () => string): string {
  const hit = cache.get(key);
  if (hit) return hit;
  const uri = toDataUri(make());
  cache.set(key, uri);
  return uri;
}

export function dieUri(skin: DiceSkin, face: DiceFace, active = false): string {
  return memo(`die:${skin}:${face}:${active}`, () => renderDie({ skin, face, active }));
}

/**
 * `seed` fixes the character's face. Pass the NPC id, never the display name —
 * renaming someone must not give them a different face.
 */
export function portraitUri(
  name: string,
  role: Parameters<typeof renderPortrait>[0]['classOrRole'],
  expression?: Parameters<typeof renderPortrait>[0]['expression'],
  seed?: string,
  showName = true,
): string {
  const key = seed ?? name;
  return memo(`portrait:${key}:${role}:${expression ?? 'neutral'}:${showName}`, () =>
    renderPortrait({
      name,
      classOrRole: role,
      seed: key,
      showName,
      ...(expression ? { expression } : {}),
    }),
  );
}

/**
 * The room you are standing in, drawn as its 512px interior. This is the art
 * `tile.ts` generates; before this it was produced on every build and never once
 * shown to a player.
 */
export function roomTileUri(room: { id: string; kind: string }): string {
  return memo(`room:${room.id}`, () =>
    renderRoomTile(room.kind as RoomKind, { seed: room.id, size: 512 }),
  );
}

/**
 * A card face. The frame, plates and rarity marks are generated; only the artwork
 * window is a slot, so a card looks finished before any illustration exists.
 */
export function cardUri(card: {
  id: string;
  type: string;
  name: string;
  text: string;
  rarity: string;
}): string {
  // `base+overlay` ids render the base card with the overlay on top — that is
  // what makes a transformed card free (Style Bible §4).
  const [base, overlay] = card.id.split('+');
  return memo(`card:${card.id}`, () =>
    renderCard({
      type: card.type as CardType,
      rarity: card.rarity as Rarity,
      name: card.name,
      text: card.text,
      seed: base ?? card.id,
      ...(overlay ? { overlay: overlay as CardOverlay } : {}),
    }),
  );
}

/**
 * A transient effect (crit burst, discovery ring, fate-debt pulse, dust, shimmer).
 * These were generated into the manifest but never drawn; `FxLayer` draws them now.
 */
export function fxUri(kind: FxKind): string {
  return memo(`fx:${kind}`, () => renderFx(kind));
}

export function iconUri(slot: ItemSlot, rarity: Rarity): string {
  return memo(`icon:${slot}:${rarity}`, () => renderItemIcon({ slot, rarity }));
}

/**
 * Map an NPC's relationship state onto one of the five expression overlays.
 * This is where NPC memory becomes visible at a glance (Style Bible §5).
 */
export function expressionFor(npc: {
  trust: number;
  resentment: number;
  alive: boolean;
}): 'neutral' | 'warm' | 'wary' | 'hostile' | 'broken' {
  if (!npc.alive) return 'broken';
  if (npc.resentment >= 50) return 'hostile';
  if (npc.trust >= 45) return 'warm';
  if (npc.trust <= -20 || npc.resentment >= 20) return 'wary';
  return 'neutral';
}
